import * as React from 'react';
import ReactDOM from 'react-dom';
import type { Placement, Instance as PopperInstance } from '@popperjs/core';
import { createPopper } from '@popperjs/core';
import { getParents } from '../../../modules/dom.utils';
import './tooltip.scss';

type Props = {
    prefix?: string;
    host?: HTMLElement;
};

type State = {
    isRendering: boolean;
};

type Attribute = 'tip' | 'tip-placement' | 'tip-delay' | 'tip-offset' | 'tip-overflow';

export default class TooltipHost extends React.Component<Props, State> {
    state: State = {
        isRendering: false
    };

    private updateTimer?: number;

    private tooltipElement?: Element;

    private tooltipRef = React.createRef<HTMLDivElement>();

    private popper?: PopperInstance;

    componentDidMount() {
        this.eventHost.addEventListener('mouseenter', this.onMouseEnter, true);
        this.eventHost.addEventListener('focusin', this.onMouseEnter, true);
        this.eventHost.addEventListener('mousemove', this.onMouseMove, true);
        this.eventHost.addEventListener('mouseleave', this.onMouseLeave, true);
        this.eventHost.addEventListener('mouseout', this.onMouseOut, true);
        this.eventHost.addEventListener('focusout', this.onMouseOut, true);
        this.eventHost.addEventListener('mousedown', this.onMouseDown, true);
        this.eventHost.addEventListener('keydown', this.onMouseDown, true);
    }

    componentDidUpdate(_prevProps: Props, prevState: State) {
        if (!prevState.isRendering && this.state.isRendering) {
            if (!this.tooltipElement) {
                return;
            }
            const placement = (this.tooltipElement.getAttribute(this.getAttributeName('tip-placement')) || 'bottom') as Placement;
            const offset = this.tooltipElement.getAttribute(this.getAttributeName('tip-offset')) || 10;
            this.popper = createPopper(this.tooltipElement!, this.tooltipRef.current!, {
                placement,
                modifiers: [
                    {
                        name: 'arrow',
                        options: {
                            element: this.eventHost.querySelector(`.tooltip-arrow`),
                            padding: 5
                        }
                    },
                    {
                        name: 'offset',
                        options: {
                            offset: [0, offset]
                        }
                    }
                ]
            });
        }
    }

    componentWillUnmount() {
        this.eventHost.removeEventListener('mouseenter', this.onMouseEnter, true);
        this.eventHost.removeEventListener('focusin', this.onMouseEnter, true);
        this.eventHost.removeEventListener('mousemove', this.onMouseMove, true);
        this.eventHost.removeEventListener('mouseleave', this.onMouseLeave, true);
        this.eventHost.removeEventListener('mouseout', this.onMouseOut, true);
        this.eventHost.removeEventListener('focusout', this.onMouseOut, true);
        this.eventHost.removeEventListener('mousedown', this.onMouseDown, true);
        this.eventHost.removeEventListener('keydown', this.onMouseDown, true);
    }

    get eventHost(): Document | HTMLElement {
        return this.props.host ?? document;
    }

    get eventHostRoot(): Document | ShadowRoot {
        const rootNode = this.props.host?.getRootNode();
        return rootNode instanceof ShadowRoot ? rootNode : document;
    }

    get text(): string | null | undefined {
        return this.tooltipElement?.getAttribute(this.getAttributeName('tip'));
    }

    getAttributeName(attribute: Attribute): string {
        const { prefix } = this.props;
        return prefix ? `data-${prefix}-${attribute}` : `data-${attribute}`;
    }

    showTooltip = () => {
        if (!this.tooltipElement) {
            return;
        }
        if (this.text) {
            this.setState({ isRendering: true });
        }
    };

    hideTooltip = () => {
        this.tooltipElement = undefined;
        window.clearTimeout(this.updateTimer);
        if (this.state.isRendering) {
            this.setState({ isRendering: false });
        }
    };

    getElement(element: Element | null): Element | undefined {
        if (!element) {
            return undefined;
        }
        const elements = getParents(element, true) as Element[];
        return elements.find((e) => 'getAttribute' in e && e.getAttribute(this.getAttributeName('tip')));
    }

    onMouseEnter = (e: Event) => {
        const currentElement = this.getElement(e.target as Element);
        if (!currentElement || e.target === this.tooltipRef.current) {
            this.hideTooltip();
            return;
        }
        if (currentElement === this.tooltipElement) {
            return;
        }
        const showOnOverflow = currentElement.getAttribute(this.getAttributeName('tip-overflow'));
        const overflowEl = showOnOverflow === 'true' ? currentElement : currentElement.querySelector(`.${showOnOverflow}`) || currentElement;
        if (showOnOverflow && overflowEl.scrollWidth <= overflowEl.clientWidth) {
            return;
        }
        this.tooltipElement = currentElement;
        const delayInt = parseInt(currentElement.getAttribute(this.getAttributeName('tip-delay')) ?? '', 10);
        const delay = Number.isNaN(delayInt) ? 500 : delayInt;
        this.updateTimer = window.setTimeout(this.showTooltip, delay);
    };

    onMouseMove = (e: Event) => {
        if (!this.tooltipElement || !(e instanceof MouseEvent)) {
            return;
        }
        if (!this.tooltipElement.hasAttribute('disabled')) {
            return;
        }
        const elements = this.eventHostRoot.elementsFromPoint(e.clientX, e.clientY);
        if (!elements.includes(this.tooltipElement)) {
            this.hideTooltip();
        }
    };

    onMouseLeave = (e: Event) => {
        if (!this.tooltipElement || !(e instanceof MouseEvent)) {
            return;
        }
        if (e.target === this.tooltipElement) {
            this.hideTooltip();
        } else {
            const elements = this.eventHostRoot.elementsFromPoint(e.clientX, e.clientY);
            if (!elements.includes(this.tooltipElement)) {
                this.hideTooltip();
            }
        }
    };

    onMouseOut = (e: Event) => {
        if (!(e instanceof MouseEvent) || !e.relatedTarget) {
            this.hideTooltip();
        }
    };

    onMouseDown = () => {
        if (this.tooltipElement) {
            this.hideTooltip();
        }
    };

    render(): React.ReactPortal | null {
        if (!this.state.isRendering) {
            return null;
        }
        this.popper?.destroy();
        return ReactDOM.createPortal(
            <div className="tooltip" ref={this.tooltipRef}>
                <div className="tooltip-arrow"></div>
                {this.text?.split('\n').map((str, index) => <div key={index}>{str}</div>)}
            </div>,
            this.props.host || document.body
        );
    }
}
